/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, LogOut, FileText, Download, FileArchive, Users, Plus, Trash2,
  UploadCloud, ClipboardList, Filter, RefreshCw, Copy, Check, KeyRound, ListChecks, FolderKanban, BarChart3, CalendarDays, ShieldCheck,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { toast } from "sonner";
import { DossiersSection, BpfSection, ChecklistSection, SessionsSection } from "./AdminPilotage";
import { QuizSection } from "./AdminQuiz";
import { ConsentSection } from "./AdminConsent";
import { FORMATIONS } from "./data/formations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminPassword = () => sessionStorage.getItem("adminPassword") || "";
const adminHeaders = () => ({ "X-Admin-Password": getAdminPassword() });

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};

const formatBytes = (b) => {
  if (!b) return "0 o";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

const downloadWithAuth = async (url, filename) => {
  try {
    const res = await axios.get(url, { headers: adminHeaders(), responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    toast.error("Téléchargement impossible");
  }
};

// ==================================================================
// Main page — 4 tabs
// ==================================================================
export const AdminAdvancedPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const t = sessionStorage.getItem("adminToken");
    const p = sessionStorage.getItem("adminPassword");
    if (t !== "admin_authenticated" || !p) navigate("/admin");
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    sessionStorage.removeItem("adminPassword");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-advanced">
      <header className="bg-[#003087] text-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-white/80 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft className="h-4 w-4" /> Retour tableau de bord
            </Link>
            <span className="hidden sm:inline text-white/40">·</span>
            <h1 className="font-heading font-bold text-lg sm:text-xl">Espace conformité & apprenants</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="admin-logout">
            <LogOut className="h-4 w-4 mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs defaultValue="qualiopi" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-10 mb-6 h-auto">
            <TabsTrigger value="qualiopi" data-testid="tab-qualiopi" className="py-3">
              <FileText className="h-4 w-4 mr-1.5 hidden sm:inline" /> Dossier Qualiopi
            </TabsTrigger>
            <TabsTrigger value="checklist" data-testid="tab-checklist" className="py-3">
              <ListChecks className="h-4 w-4 mr-1.5 hidden sm:inline" /> Progression Qualiopi
            </TabsTrigger>
            <TabsTrigger value="dossiers" data-testid="tab-dossiers" className="py-3">
              <FolderKanban className="h-4 w-4 mr-1.5 hidden sm:inline" /> Dossiers stagiaires
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions" className="py-3">
              <CalendarDays className="h-4 w-4 mr-1.5 hidden sm:inline" /> Sessions
            </TabsTrigger>
            <TabsTrigger value="bpf" data-testid="tab-bpf" className="py-3">
              <BarChart3 className="h-4 w-4 mr-1.5 hidden sm:inline" /> BPF
            </TabsTrigger>
            <TabsTrigger value="apprenants" data-testid="tab-apprenants" className="py-3">
              <Users className="h-4 w-4 mr-1.5 hidden sm:inline" /> Apprenants
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources" className="py-3">
              <UploadCloud className="h-4 w-4 mr-1.5 hidden sm:inline" /> Ressources
            </TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz" className="py-3">
              <ListChecks className="h-4 w-4 mr-1.5 hidden sm:inline" /> Quiz & progression
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" className="py-3">
              <ClipboardList className="h-4 w-4 mr-1.5 hidden sm:inline" /> Journal d'accès
            </TabsTrigger>
            <TabsTrigger value="consent" data-testid="tab-consent" className="py-3">
              <ShieldCheck className="h-4 w-4 mr-1.5 hidden sm:inline" /> Consentements
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qualiopi"><QualiopiSection /></TabsContent>
          <TabsContent value="checklist"><ChecklistSection /></TabsContent>
          <TabsContent value="dossiers"><DossiersSection /></TabsContent>
          <TabsContent value="sessions"><SessionsSection /></TabsContent>
          <TabsContent value="bpf"><BpfSection /></TabsContent>
          <TabsContent value="apprenants"><ApprenantsSection /></TabsContent>
          <TabsContent value="resources"><ResourcesSection /></TabsContent>
          <TabsContent value="quiz"><QuizSection /></TabsContent>
          <TabsContent value="consent"><ConsentSection /></TabsContent>
          <TabsContent value="logs"><LogsSection /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ==================================================================
// Qualiopi tab
// ==================================================================
const QualiopiSection = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/qualiopi/docs`, { headers: adminHeaders() });
      setDocs(res.data.docs || []);
    } catch {
      toast.error("Impossible de charger les documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="text-[#003087]">Dossier Qualiopi — 15 documents</CardTitle>
            <CardDescription>
              Trames officielles rédigées pour l'audit. Téléchargeables en PDF (auditeur) ou DOCX (modifiable).
            </CardDescription>
          </div>
          <Button
            onClick={() => downloadWithAuth(`${API}/admin/qualiopi/all.zip`, `MGB-School_Dossier-Qualiopi_${new Date().toISOString().slice(0,10)}.zip`)}
            className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold"
            data-testid="btn-download-all-zip"
          >
            <FileArchive className="h-4 w-4 mr-2" /> Tout télécharger (ZIP)
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-500 text-sm">Chargement…</p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex flex-col md:flex-row md:items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-[#F07B1D]/40 bg-white"
                data-testid={`qualiopi-doc-${d.id}`}
              >
                <FileText className="h-5 w-5 text-[#003087] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#003087] text-sm leading-snug">{d.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 font-mono truncate">{d.filename} · {d.size_kb} Ko</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white"
                    onClick={() => downloadWithAuth(`${API}/admin/qualiopi/pdf/${d.id}`, `MGB-School_Qualiopi_${d.id}.pdf`)}
                    data-testid={`pdf-btn-${d.id}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#F07B1D] text-[#F07B1D] hover:bg-[#F07B1D] hover:text-white"
                    onClick={() => downloadWithAuth(`${API}/admin/qualiopi/docx/${d.id}`, `MGB-School_Qualiopi_${d.id}.docx`)}
                    data-testid={`docx-btn-${d.id}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> DOCX
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================================================================
// Apprenants tab
// ==================================================================
const ApprenantsSection = () => {
  const [apprenants, setApprenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", prenom: "", nom: "", formation_code: "", formation_title: "" });
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/apprenants`, { headers: adminHeaders() });
      setApprenants(res.data || []);
    } catch { toast.error("Erreur chargement apprenants"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/apprenants`, form, { headers: adminHeaders() });
      setCreated(res.data);
      setForm({ email: "", prenom: "", nom: "", formation_code: "", formation_title: "" });
      load();
      toast.success("Apprenant créé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Création impossible");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet apprenant ?")) return;
    try {
      await axios.delete(`${API}/admin/apprenants/${id}`, { headers: adminHeaders() });
      load();
      toast.success("Apprenant supprimé");
    } catch { toast.error("Suppression impossible"); }
  };

  const handleResetPassword = async (id, email) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${email} ?`)) return;
    try {
      const res = await axios.post(`${API}/admin/apprenants/${id}/reset-password`, {}, { headers: adminHeaders() });
      setCreated({ email, generated_password: res.data.generated_password, prenom: "", nom: "" });
      toast.success("Mot de passe réinitialisé");
    } catch { toast.error("Réinitialisation impossible"); }
  };

  const copyCredentials = () => {
    if (!created) return;
    const text = `E-mail : ${created.email}\nMot de passe : ${created.generated_password}\nPortail : ${window.location.origin}/apprenant`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {created && (
        <Card className="border-2 border-[#F07B1D] bg-[#FFF4E6]" data-testid="new-credentials-card">
          <CardHeader>
            <CardTitle className="text-[#003087] flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Identifiants générés
            </CardTitle>
            <CardDescription className="text-[#7A3B00]">
              ⚠️ Ces identifiants ne seront affichés qu'une seule fois. Communiquez-les à l'apprenant maintenant.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3 bg-white p-4 rounded-lg border border-[#F07B1D]/40">
              <div>
                <Label className="text-xs text-gray-500">E-mail</Label>
                <p className="font-mono text-sm font-bold text-[#003087]" data-testid="new-cred-email">{created.email}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Mot de passe</Label>
                <p className="font-mono text-sm font-bold text-[#003087]" data-testid="new-cred-password">{created.generated_password}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyCredentials} className="bg-[#003087] hover:bg-[#002266] text-white" data-testid="copy-creds-btn">
                {copied ? <><Check className="h-4 w-4 mr-1" /> Copié</> : <><Copy className="h-4 w-4 mr-1" /> Copier les identifiants</>}
              </Button>
              <Button variant="outline" onClick={() => setCreated(null)}>Fermer</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[#003087]">Créer un apprenant</CardTitle>
          <CardDescription>Un mot de passe sera généré automatiquement.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Prénom *</Label>
              <Input required value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="apprenant-form-prenom" />
            </div>
            <div>
              <Label>Nom *</Label>
              <Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="apprenant-form-nom" />
            </div>
            <div className="md:col-span-2">
              <Label>E-mail *</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apprenant-form-email" />
            </div>
            <div>
              <Label>Code formation (ex : F04)</Label>
              <Input value={form.formation_code} onChange={(e) => setForm({ ...form, formation_code: e.target.value })} data-testid="apprenant-form-code" />
            </div>
            <div>
              <Label>Intitulé formation</Label>
              <Input value={form.formation_title} onChange={(e) => setForm({ ...form, formation_title: e.target.value })} data-testid="apprenant-form-title" />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="apprenant-create-btn">
                <Plus className="h-4 w-4 mr-1" /> Créer l'apprenant
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-[#003087]">Apprenants inscrits</CardTitle>
            <CardDescription>{apprenants.length} compte(s)</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-gray-500">Chargement…</p> : apprenants.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Aucun apprenant pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="apprenants-table">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                    <th className="py-2 pr-2">Nom</th>
                    <th className="py-2 pr-2">E-mail</th>
                    <th className="py-2 pr-2">Formation</th>
                    <th className="py-2 pr-2">Créé le</th>
                    <th className="py-2 pr-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apprenants.map((a) => (
                    <tr key={a.id} className="border-b border-gray-100" data-testid={`apprenant-row-${a.id}`}>
                      <td className="py-2 pr-2 font-semibold text-[#003087]">{a.prenom} {a.nom}</td>
                      <td className="py-2 pr-2 text-gray-600">{a.email}</td>
                      <td className="py-2 pr-2">
                        {a.formation_code ? <Badge variant="outline">{a.formation_code}</Badge> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-2 text-xs text-gray-500">{formatDate(a.created_at)}</td>
                      <td className="py-2 pr-2 flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleResetPassword(a.id, a.email)} title="Réinitialiser mot de passe">
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(a.id)} className="text-red-600 border-red-200 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================================
// Resources tab
// ==================================================================
const ResourcesSection = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", category: "modele", formation_codes: "", module_id: "", file: null });
  const [uploading, setUploading] = useState(false);
  const moduleOptions = FORMATIONS.filter((f) => form.formation_codes.split(",").map((c) => c.trim()).includes(f.code))
    .flatMap((f) => (f.programme || []).map((m, i) => ({ id: `${f.code}-M${i + 1}`, label: `${f.code} · ${m.title}` })));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/resources`, { headers: adminHeaders() });
      setResources(res.data || []);
    } catch { toast.error("Erreur chargement ressources"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Sélectionnez un fichier");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("formation_codes", form.formation_codes);
      fd.append("module_id", form.module_id);
      fd.append("file", form.file);
      await axios.post(`${API}/admin/resources`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Ressource ajoutée");
      setForm({ title: "", description: "", category: "modele", formation_codes: "", module_id: "", file: null });
      // Reset file input
      const input = document.getElementById("resource-file-input");
      if (input) input.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload impossible");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette ressource ?")) return;
    try {
      await axios.delete(`${API}/admin/resources/${id}`, { headers: adminHeaders() });
      load();
      toast.success("Ressource supprimée");
    } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-[#003087]">Ajouter une ressource</CardTitle>
          <CardDescription>
            Modèles de factures, tableurs, checklists, livrets. Rendus disponibles dans l'espace apprenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Titre *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Modèle de facture – services" data-testid="resource-form-title" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Conforme article 289 CGI" data-testid="resource-form-description" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="resource-form-category"
              >
                <option value="modele">Modèle (facture, contrat…)</option>
                <option value="tableur">Tableur / simulation</option>
                <option value="checklist">Checklist</option>
                <option value="livret">Livret / support</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <Label>Codes formation (séparés par virgule, vide = tous)</Label>
              <Input value={form.formation_codes} onChange={(e) => setForm({ ...form, formation_codes: e.target.value, module_id: "" })} placeholder="F04, F09" data-testid="resource-form-codes" />
            </div>
            <div className="md:col-span-2">
              <Label>Rattacher à un module (facultatif — apparaît dans le parcours de l'apprenant)</Label>
              <select
                value={form.module_id}
                onChange={(e) => setForm({ ...form, module_id: e.target.value })}
                disabled={moduleOptions.length === 0}
                className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="resource-form-module"
              >
                <option value="">{moduleOptions.length ? "Support général de la formation" : "Saisissez d'abord un code formation"}</option>
                {moduleOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Fichier *</Label>
              <input
                id="resource-file-input"
                type="file"
                required
                onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                className="block w-full text-sm mt-1"
                data-testid="resource-form-file"
              />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={uploading} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="resource-upload-btn">
                <UploadCloud className="h-4 w-4 mr-1" /> {uploading ? "Envoi…" : "Ajouter la ressource"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-[#003087]">Ressources disponibles</CardTitle>
            <CardDescription>{resources.length} fichier(s)</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-gray-500">Chargement…</p> : resources.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Aucune ressource pour l'instant.</p>
          ) : (
            <div className="space-y-2">
              {resources.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white" data-testid={`resource-row-${r.id}`}>
                  <UploadCloud className="h-5 w-5 text-[#003087] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#003087] text-sm">{r.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {r.original_filename} · {formatBytes(r.size_bytes)} · {r.category}
                      {r.formation_codes.length > 0 && ` · ${r.formation_codes.join(", ")}`}
                      {r.formation_codes.length === 0 && " · Tous"}
                      {r.module_id && ` · module ${r.module_id.match(/M\d+$/)?.[0]}`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)} className="text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================================
// Logs tab
// ==================================================================
const LogsSection = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ event_type: "", apprenant_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "300");
      if (filter.event_type) params.append("event_type", filter.event_type);
      if (filter.apprenant_id) params.append("apprenant_id", filter.apprenant_id);
      const res = await axios.get(`${API}/admin/logs?${params.toString()}`, { headers: adminHeaders() });
      setLogs(res.data || []);
    } catch { toast.error("Erreur chargement journal"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filter.event_type) params.append("event_type", filter.event_type);
    if (filter.apprenant_id) params.append("apprenant_id", filter.apprenant_id);
    downloadWithAuth(
      `${API}/admin/logs/export.csv?${params.toString()}`,
      `MGB-School_journal_acces_${new Date().toISOString().slice(0,10)}.csv`,
    );
  };

  const eventColor = (type) => {
    if (type.startsWith("login_success") || type.includes("_complete")) return "bg-green-100 text-green-800 border-green-200";
    if (type.startsWith("login_failed") || type.includes("error")) return "bg-red-100 text-red-800 border-red-200";
    if (type.includes("download")) return "bg-blue-100 text-blue-800 border-blue-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="text-[#003087]">Journal d'accès</CardTitle>
            <CardDescription>
              Traçabilité complète des connexions, téléchargements et complétions — preuve pour l'audit Qualiopi.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
            <Button onClick={exportCsv} className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white" data-testid="logs-export-csv-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#003087]"><Filter className="h-4 w-4" /> Filtres</div>
          <select
            value={filter.event_type}
            onChange={(e) => setFilter({ ...filter, event_type: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
            data-testid="logs-filter-event"
          >
            <option value="">Tous les événements</option>
            <option value="login_success">Connexion réussie</option>
            <option value="login_failed">Connexion échouée</option>
            <option value="resource_download">Téléchargement</option>
            <option value="module_start">Début de module</option>
            <option value="module_complete">Module terminé</option>
            <option value="quiz_attempt">Tentative quiz</option>
            <option value="quiz_result">Résultat quiz</option>
            <option value="page_view">Vue de page</option>
          </select>
          <Input
            placeholder="ID apprenant (optionnel)"
            value={filter.apprenant_id}
            onChange={(e) => setFilter({ ...filter, apprenant_id: e.target.value })}
            className="max-w-xs text-sm"
            data-testid="logs-filter-apprenant"
          />
        </div>

        {loading ? <p className="text-sm text-gray-500">Chargement…</p> : logs.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Aucun événement.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="logs-table">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-2 pr-2 whitespace-nowrap">Date / heure</th>
                  <th className="py-2 pr-2">Événement</th>
                  <th className="py-2 pr-2">Apprenant</th>
                  <th className="py-2 pr-2 hidden md:table-cell">IP</th>
                  <th className="py-2 pr-2 hidden md:table-cell">Détail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100" data-testid={`log-row-${l.id}`}>
                    <td className="py-2 pr-2 font-mono text-[11px] whitespace-nowrap">{formatDate(l.timestamp)}</td>
                    <td className="py-2 pr-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${eventColor(l.event_type)}`}>
                        {l.event_type}
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-gray-700">{l.apprenant_email || <em className="text-gray-400">—</em>}</td>
                    <td className="py-2 pr-2 hidden md:table-cell font-mono text-[11px] text-gray-500">{l.ip || "—"}</td>
                    <td className="py-2 pr-2 hidden md:table-cell text-[11px] text-gray-500 max-w-xs truncate">
                      {l.metadata && Object.keys(l.metadata).length > 0 ? JSON.stringify(l.metadata) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAdvancedPage;
